import { useEffect, useId, useRef, type ButtonHTMLAttributes, type ComponentType, type ReactNode, type SVGProps } from 'react'
import { CloudOff, CloudUpload, LoaderCircle, MapPin, Navigation, Upload } from 'lucide-react'

type Icon = ComponentType<SVGProps<SVGSVGElement> & { size?: string | number; strokeWidth?: string | number }>

export function Button({
  variant = 'primary', size = 'md', loading = false, icon: IconComponent, children, className = '', disabled, ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  icon?: Icon
}) {
  return <button
    {...props}
    disabled={disabled || loading}
    aria-busy={loading || undefined}
    className={`cht-button cht-button--${variant} cht-button--${size} ${className}`.trim()}
  >
    {loading ? <><LoaderCircle aria-hidden="true" className="spin" size={18} /><span>WORKING…</span></> : <>{IconComponent && <IconComponent aria-hidden="true" size={size === 'lg' ? 20 : 18} />} {children}</>}
  </button>
}

export function IconButton({ label, icon: IconComponent, pressed, className = '', ...props }: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & { label: string; icon: Icon; pressed?: boolean }) {
  return <button {...props} type="button" aria-label={label} aria-pressed={pressed} className={`cht-icon-button ${className}`.trim()}>
    <IconComponent aria-hidden="true" size={20} />
  </button>
}

export function SectionEyebrow({ children, count }: { children: ReactNode; count?: number }) {
  return <div className="section-eyebrow"><span>{children}</span>{count !== undefined && <span>{count}</span>}</div>
}

export function InlineNotice({ tone = 'info', title, children, action }: { tone?: 'info' | 'pending' | 'fail' | 'ok'; title?: string; children: ReactNode; action?: ReactNode }) {
  return <div className={`inline-notice inline-notice--${tone}`} role={tone === 'fail' ? 'alert' : 'status'} aria-live="polite">
    {title && <p className="inline-notice__title">{title}</p>}
    <div>{children}</div>
    {action && <div className="inline-notice__action">{action}</div>}
  </div>
}

export function ConfirmSheet({ title, children, actions, onCancel }: { title: string; children: ReactNode; actions: ReactNode; onCancel: () => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const titleId = useId()
  useEffect(() => {
    const sheet = ref.current
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : undefined
    const focusable = sheet?.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')
    focusable?.[0]?.focus()
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') { event.preventDefault(); onCancel(); return }
      if (event.key !== 'Tab' || !focusable?.length) return
      const first = focusable[0]!, last = focusable[focusable.length - 1]!
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => { document.removeEventListener('keydown', onKeyDown); previousFocus?.focus() }
  }, [])
  return <div ref={ref} className="confirmation-card" role="alertdialog" aria-modal="true" aria-labelledby={titleId}>
    <h2 id={titleId}>{title}</h2><div>{children}</div><div className="button-row">{actions}</div>
  </div>
}

export function StatusStrip({ online, tracking, accuracyMeters, queued = 0, fence = 'checking', lastSync, onQueueClick }: {
  online: boolean
  tracking: boolean
  accuracyMeters?: number
  queued?: number
  fence?: 'checking' | 'inside' | 'outside'
  lastSync?: string
  onQueueClick?: () => void
}) {
  const fenceText = fence === 'inside' ? 'ON PROPERTY' : fence === 'outside' ? 'OFF PROPERTY' : 'CHECKING FENCE'
  const items = [
    { key: 'network', Icon: online ? CloudUpload : CloudOff, text: online ? 'READY' : 'OFFLINE', tone: online ? 'ok' : 'pending' },
    { key: 'gps', Icon: Navigation, text: tracking && accuracyMeters !== undefined ? `GPS ±${Math.round(accuracyMeters)}m` : 'GPS OFF', tone: tracking ? 'ok' : 'dim' },
    { key: 'queue', Icon: Upload, text: `QUEUE ${queued}`, tone: queued ? 'pending' : 'dim', action: onQueueClick },
    { key: 'fence', Icon: MapPin, text: tracking ? fenceText : `SYNC ${lastSync ?? '--'}`, tone: 'dim' }
  ]
  return <header className="status-strip" aria-live="polite">
    {items.map(({ key, Icon: StatusIcon, text, tone, action }) => action ? <button type="button" key={key} className={`status-strip__item status-strip__item--${tone} status-strip__action`} onClick={action}>
      <StatusIcon aria-hidden="true" size={14} strokeWidth={1.75} /><span>{text}</span>
    </button> : <span key={key} className={`status-strip__item status-strip__item--${tone}`}><StatusIcon aria-hidden="true" size={14} strokeWidth={1.75} /><span>{text}</span></span>)}
  </header>
}

export interface RailItem { id: string; label: string; icon: Icon; disabled?: boolean }
export interface RailSection { label: string; items: RailItem[] }

export function ContextRail({ sections, activeId, onSelect, logoSrc }: { sections: RailSection[]; activeId: string; onSelect: (id: string) => void; logoSrc: string }) {
  return <nav className="context-rail" aria-label="Sections">
    <div className="context-rail__brand"><img src={logoSrc} alt="" /><span>Country<br />House Trails</span></div>
    {sections.map((section) => <div key={section.label} className="context-rail__section">
      <p>{section.label}</p>
      {section.items.map(({ id, label, icon: RailIcon, disabled }) => <button
        key={id}
        type="button"
        aria-current={id === activeId ? 'page' : undefined}
        disabled={disabled}
        onClick={() => onSelect(id)}
      ><RailIcon aria-hidden="true" size={17} strokeWidth={1.75} /><span>{label}</span></button>)}
    </div>)}
  </nav>
}
